import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, Search } from 'lucide-react';
import { PartnershipFull, UpdatePartnershipRequest } from '../../types/partnership';
import { partnershipsApiService } from '../../services/partnershipsApi';
import { useStaffListSearch } from '../../hooks/useStaffListSearch';
import { usernameCacheService, useUsernameCacheVersion } from '../../services/usernameCache';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useDebounce } from '../../hooks/useDebounce';
import { PlayerProfile } from '../PlayerProfile';
import { cn } from '../../utils/cn';
import { format } from 'date-fns';

export interface EditPartnershipModalProps {
  isOpen: boolean;
  partnership: PartnershipFull;
  onClose: () => void;
  onSuccess: () => void;
}

function toDatetimeLocal(ts: number | null): string {
  if (ts == null) return '';
  const date = ts > 1e12 ? new Date(ts) : new Date(ts * 1000);
  return format(date, "yyyy-MM-dd'T'HH:mm");
}

function fromDatetimeLocal(value: string): number | null {
  if (!value.trim()) return null;
  const ms = new Date(value).getTime();
  return isNaN(ms) ? null : ms;
}

export function EditPartnershipModal({
  isOpen,
  partnership,
  onClose,
  onSuccess,
}: EditPartnershipModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [summary, setSummary] = useState(partnership.summary ?? '');
  const [priority, setPriority] = useState(partnership.priority);
  const [assignedTo, setAssignedTo] = useState(partnership.assigned_to ?? '');
  const [dealTerms, setDealTerms] = useState(
    partnership.deal_terms
      ? (() => {
          try {
            return JSON.stringify(JSON.parse(partnership.deal_terms), null, 2);
          } catch {
            return partnership.deal_terms;
          }
        })()
      : ''
  );
  const [contactDate, setContactDate] = useState(toDatetimeLocal(partnership.contact_date));
  const [responseDate, setResponseDate] = useState(toDatetimeLocal(partnership.response_date));
  const [startDate, setStartDate] = useState(toDatetimeLocal(partnership.start_date));
  const [endDate, setEndDate] = useState(toDatetimeLocal(partnership.end_date));
  const [declineReason, setDeclineReason] = useState(partnership.decline_reason ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && partnership) {
      setSummary(partnership.summary ?? '');
      setPriority(partnership.priority);
      setAssignedTo(partnership.assigned_to ?? '');
      setDealTerms(
        partnership.deal_terms
          ? (() => {
              try {
                return JSON.stringify(JSON.parse(partnership.deal_terms), null, 2);
              } catch {
                return partnership.deal_terms;
              }
            })()
          : ''
      );
      setContactDate(toDatetimeLocal(partnership.contact_date));
      setResponseDate(toDatetimeLocal(partnership.response_date));
      setStartDate(toDatetimeLocal(partnership.start_date));
      setEndDate(toDatetimeLocal(partnership.end_date));
      setDeclineReason(partnership.decline_reason ?? '');
      setError(null);
    }
  }, [isOpen, partnership]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      let parsedDealTerms: string | null = null;
      if (dealTerms.trim()) {
        try {
          parsedDealTerms = JSON.stringify(JSON.parse(dealTerms));
        } catch {
          setError('Deal terms must be valid JSON');
          setIsSubmitting(false);
          return;
        }
      }

      const data: UpdatePartnershipRequest = {
        summary: summary.trim() || null,
        priority,
        assigned_to: assignedTo.trim() || null,
        deal_terms: parsedDealTerms,
        contact_date: fromDatetimeLocal(contactDate),
        response_date: fromDatetimeLocal(responseDate),
        start_date: fromDatetimeLocal(startDate),
        end_date: fromDatetimeLocal(endDate),
        decline_reason: declineReason.trim() || null,
      };

      await partnershipsApiService.updatePartnership(partnership.id, data);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update partnership');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-glass-400 hover:text-white p-1"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-4">Edit Partnership</h2>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-red-300 text-sm">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Summary</label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="Brief partnership summary..."
              rows={3}
            />
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Priority (1–10)</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={1}
                max={10}
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="flex-1 h-2 rounded-full appearance-none bg-glass-200/30 accent-accent-500"
              />
              <input
                type="number"
                min={1}
                max={10}
                value={priority}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (v >= 1 && v <= 10) setPriority(v);
                }}
                className="glass-input w-14 text-sm text-center"
              />
            </div>
          </div>

          <AssigneeSearchField value={assignedTo} onChange={setAssignedTo} />

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Deal terms (JSON)</label>
            <textarea
              value={dealTerms}
              onChange={(e) => setDealTerms(e.target.value)}
              className="glass-input w-full text-sm font-mono"
              placeholder='{"key": "value"}'
              rows={6}
            />
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Contact date</label>
            <input
              type="datetime-local"
              value={contactDate}
              onChange={(e) => setContactDate(e.target.value)}
              className="glass-input w-full text-sm"
            />
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Response date</label>
            <input
              type="datetime-local"
              value={responseDate}
              onChange={(e) => setResponseDate(e.target.value)}
              className="glass-input w-full text-sm"
            />
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Start date</label>
            <input
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="glass-input w-full text-sm"
            />
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">End date</label>
            <input
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="glass-input w-full text-sm"
            />
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Decline reason</label>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="Reason for decline (if applicable)"
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-glass-200/20">
            <button
              type="button"
              onClick={onClose}
              className="glass-button-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={cn('glass-button flex items-center gap-2', isSubmitting && 'opacity-70 cursor-not-allowed')}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : null}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

function AssigneeSearchField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const { matches, searching, error: staffSearchError } = useStaffListSearch({
    debouncedQuery,
    enabled: true,
  });
  const usernameCacheVersion = useUsernameCacheVersion();
  const results = useMemo(
    () =>
      matches.map((s) => ({
        uuid: s.minecraft_uuid,
        name:
          usernameCacheService.getName(s.minecraft_uuid) ?? s.minecraft_uuid,
      })),
    [matches, usernameCacheVersion]
  );

  return (
    <div>
      <label className="text-sm text-glass-300 mb-1 block">Assigned to</label>
      {value && (
        <div className="flex items-center justify-between gap-2 mb-2 px-2 py-1.5 rounded-lg bg-accent-500/10 border border-accent-500/20">
          <PlayerProfile uuid={value} inline showUuid={false} />
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-glass-500">{value.slice(0, 8)}...</span>
            <button type="button" onClick={() => onChange('')} className="text-glass-400 hover:text-red-400 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-glass-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
          onFocus={() => setShowResults(true)}
          className="glass-input w-full text-sm pl-8"
          placeholder={value ? 'Search to reassign...' : 'Search by username...'}
          autoComplete="off"
        />
        {searching && <Loader className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-glass-400" />}
      </div>
      {showResults && results.length > 0 && (
        <div className="mt-1 rounded-lg border border-glass-200/20 overflow-hidden max-h-[180px] overflow-y-auto bg-dark-900/90">
          {results.map((player) => (
            <button
              key={player.uuid}
              type="button"
              onClick={() => {
                onChange(player.uuid);
                setQuery('');
                setShowResults(false);
              }}
              className={cn(
                'w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-glass-200/10 transition-colors',
                player.uuid === value && 'bg-accent-500/10'
              )}
            >
              <PlayerProfile uuid={player.uuid} name={player.name} inline showUuid={false} />
              <span className="text-[10px] text-glass-500 font-mono flex-shrink-0">{player.uuid.slice(0, 8)}...</span>
            </button>
          ))}
        </div>
      )}
      {staffSearchError && (
        <p className="text-xs text-red-400 mt-1 px-1">{staffSearchError}</p>
      )}
      {showResults && debouncedQuery.length >= 2 && !searching && !staffSearchError && results.length === 0 && (
        <p className="text-xs text-glass-500 mt-1 px-1">No staff found</p>
      )}
    </div>
  );
}
